import { dcmjsGetString } from '~/domain/dicom/dcmjsAccess';
import {
    DeformableGrid,
    DicomDeformableRegistration,
} from '~/domain/dicom/dicomDeformableRegistration';
import { DeformableTransform } from '~/domain/dvf/deformableTransform';
import { DvfField, parseDvfField } from '~/domain/dvf/dvfField';
import { rowMajorArrayToMat4 } from '~/domain/dicom/utils';
import { logErrorName } from '~/utils/logErrorName';

const fieldCache = new WeakMap<DeformableGrid, DvfField>();

export function deformableGridToDvfField(grid: DeformableGrid): DvfField {
    const cached = fieldCache.get(grid);
    if (cached) {
        return cached;
    }
    // The grid's own bytes: `.buffer` may be a view onto the whole DICOM file.
    const data = grid.data;
    const bytes =
        data.byteOffset === 0 && data.byteLength === data.buffer.byteLength
            ? (data.buffer as ArrayBuffer)
            : (data.buffer.slice(
                  data.byteOffset,
                  data.byteOffset + data.byteLength
              ) as ArrayBuffer);
    const field = parseDvfField(bytes, {
        size: grid.size,
        spacing: grid.spacing,
        origin: grid.origin,
        direction: directionFromOrientation(grid.orientation),
    });
    const result = grid.metrics ? { ...field, metrics: grid.metrics } : field;
    fieldCache.set(grid, result);
    return result;
}

export function directionFromOrientation(orientation: number[]): number[] {
    if (orientation.length !== 6 || !orientation.every(Number.isFinite)) {
        throw new Error(
            `Deformable grid orientation must be 6 finite direction cosines, got [${orientation.join(', ')}]`
        );
    }
    const [rx, ry, rz, cx, cy, cz] = orientation;
    // Third column = row x column, giving a right-handed frame.
    const [nx, ny, nz] = [
        ry * cz - rz * cy,
        rz * cx - rx * cz,
        rx * cy - ry * cx,
    ];
    // Row-major, columns being (row cosine, column cosine, normal).
    return [rx, cx, nx, ry, cy, ny, rz, cz, nz];
}

export function toDvfField(
    registration: { readonly grid: DeformableGrid } | undefined
): DvfField | undefined {
    if (!registration) {
        return undefined;
    }
    try {
        return deformableGridToDvfField(registration.grid);
    } catch (error) {
        // Callers read undefined as "no deformation"; this log is what tells the two apart.
        logErrorName(
            'Ignoring a deformable registration whose grid cannot be read:',
            error
        );
        return undefined;
    }
}

export function registrationSeriesDescription(
    registration: DicomDeformableRegistration
): string {
    return dcmjsGetString(registration.datasetWrapper, 'SeriesDescription');
}

export function toDeformableTransform(
    registration: { readonly grid: DeformableGrid } | undefined
): DeformableTransform | undefined {
    const field = toDvfField(registration);
    if (!field || !registration) {
        return undefined;
    }
    return {
        field,
        pre: rowMajorArrayToMat4(registration.grid.preDeformationMatrix),
        post: rowMajorArrayToMat4(registration.grid.postDeformationMatrix),
    };
}
